import { useFormik } from "formik";
import * as Yup from "yup";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { updateConfig } from "../../../../store/slices/User/SplashScreen/splashScreenSlice";
import { toast } from "react-toastify";
import { getSplashScreen, createSplashScreen } from "../api";
import { useEffect, useState } from "react";
import { phoneRegExp } from "../../../../utils/Functions/table";

const useAddSplashScreen = ({ handleCreateClick, refetch }) => {
  const {
    isEdit,
    videoName,
    isLoading,
    selectedId,
    videoPreview,
    isLoadingVideo,
    arabicVideoName,
    editDataLoading,
    arabicVideoPreview,
    editSplashScreenData,
    isArabicVideoLoading,
  } = useSelector((state) => state.splash);
  const dispatch = useDispatch();
  const [showAudienceWarning, setShowAudienceWarning] = useState(false);

  /**
   * This screen is the live all-users one, and the pending edit would take it away - either by
   * attaching numbers to it or by switching it off. Nothing else would step in, so customers on
   * no list would see no splash screen at all. Worth a word, not worth blocking.
   */
  const leavesCommonUsersWithNothing = (values) =>
    Boolean(editSplashScreenData?.is_active_for_all_users) &&
    ((values?.user_mobiles?.length ?? 0) > 0 || Number(values?.status) !== 1);

  const validation = Yup.object({
    start_date: Yup.string().required("Please enter from date"),
    end_date: Yup.string().required("Please enter to date"),
    media_path: Yup.string().required("Upload video file"),
    alias_media_path: Yup.string().required("Upload video file"),
    add_user: Yup.string()
      .matches(phoneRegExp, "Please enter a valid phone number")
      .min(8, "Number should be at least 8 characters long")
      .max(8, "Number should be 8 characters long"),
    user_mobiles: Yup.array(),
  });
  const formik = useFormik({
    initialValues: {
      button_color: "#79d8ee",
      text_color: "#ffffff",
      start_date: "",
      end_date: "",
      media_path: "",
      alias_media_path: "",
      status: 2,
      add_user: "",
      user_mobiles: [],
    },
    validationSchema: validation,
    onSubmit: async (values) => {
      if (leavesCommonUsersWithNothing(values)) {
        setShowAudienceWarning(true);
        return;
      }
      await saveSplashScreen(values);
    },
  });

  const saveSplashScreen = async (values) => {
    let data = {
      button_color: values?.button_color ?? "",
      text_color: values?.text_color ?? "",
      start_date: values?.start_date ?? "",
      end_date: values?.end_date ?? "",
      media_path: values?.media_path ?? "",
      alias_media_path: values?.alias_media_path ?? "",
      status: values?.status ?? 2,
      user_mobiles:values?.user_mobiles,
    };
    const updateData = { splash_screen_id: selectedId, ...data };
    dispatch(
      updateConfig((state) => {
        state.isLoading = true;
      })
    );
    const response = await createSplashScreen(isEdit ? updateData : data);
    try {
      if (response?.data?.status_code === 200) {
        toast.success(
          response?.data?.message ??
            `Splash screen ${isEdit ? "updated" : "created"} successfully`
        );
        handleCreateClick();
        dispatch(
          updateConfig((state) => {
            state.clearSelection = true;
          })
        );
        refetch();
      } else if (
        response?.data?.errors &&
        response?.data?.status_code === 422
      ) {
        Object.keys(response?.data?.errors).forEach((field) => {
          formik.setFieldError(field, response?.data?.errors[field]);
        });
        dispatch(updateConfig((state) => (state.clearSelection = false)));
      } else {
        toast.error(`Something went wrong! Please try again`);
        console.error("Error:", response?.errors);
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      dispatch(updateConfig((state) => (state.isLoading = false)));
    }
  };

  const handleConfirmAudienceWarning = async () => {
    setShowAudienceWarning(false);
    await saveSplashScreen(formik.values);
  };

  const handleCancelAudienceWarning = () => setShowAudienceWarning(false);

  const getFieldError = (fieldName) => {
    if (formik.touched[fieldName] && formik.errors[fieldName]) {
      return formik.errors[fieldName];
    }
    return "";
  };
  const closeModal = () => {
    setShowAudienceWarning(false);
    dispatch(
      updateConfig((state) => {
        state.showCreateModal = false;
        state.isEdit = false;
        state.editSplashScreenData = {};
        state.videoPreview = "";
        state.arabicVideoPreview = "";
        state.videoName = "";
        state.arabicVideoName = "";
        state.isLoadingVideo = false;
        state.isArabicVideoLoading = false;
      })
    );
  };

  useEffect(() => {
    if (isEdit && editSplashScreenData) {
      formik?.setFieldValue("start_date", editSplashScreenData?.start_date);
      formik?.setFieldValue("end_date", editSplashScreenData?.end_date);
      formik?.setFieldValue("media_path", editSplashScreenData?.media_path);
      formik?.setFieldValue(
        "alias_media_path",
        editSplashScreenData?.alias_media_path
      );
      formik?.setFieldValue("status", editSplashScreenData?.status);
      formik?.setFieldValue("button_color", editSplashScreenData?.button_color);
      formik?.setFieldValue("text_color", editSplashScreenData?.text_color);
      formik?.setFieldValue("user_mobiles", editSplashScreenData?.user_mobiles);
      dispatch(
        updateConfig((state) => {
          state.videoName = editSplashScreenData?.media_path?.split("/").pop();
          state.arabicVideoName = editSplashScreenData?.alias_media_path
            ?.split("/")
            .pop();
        })
      );
    }
    //eslint-disable-next-line
  }, [isEdit, editSplashScreenData, editDataLoading]);

  const handleVideoChange = async (event, fieldName) => {
    const selectedFile = event?.target?.files[0];
    // for conditional Loading of the Video
    const allowedFormats = [
      "video/mp4",
      "video/mpeg",
      "video/quicktime",
      "video/3gpp",
      "video/avi",
      "video/x-msvideo",
      "video/x-flv",
      "video/x-ms-wmv",
      "video/x-matroska",
    ]; // Add more video formats as needed
    const maxSizeInBytes = 10 * 1024 * 1024; //
    const isMainVideo = fieldName === "media_path";
    if (selectedFile) {
      let validFiles = [];
      if (!allowedFormats.includes(selectedFile.type)) {
        toast.error(
          // `${fieldName === "media_path" ? "media_path" : "alias_media_path"}`,
          `File ${selectedFile.name} is not a valid video format`
        );
        return;
      } else if (selectedFile?.size > maxSizeInBytes) {
        toast.error(
          // `${fieldName === "media_path" ? "media_path" : "alias_media_path"}`,
          `File ${selectedFile.name} exceeds the 10 MB limit`
        );
        return;
      } else {
        validFiles.push(selectedFile);
      }
      const formData = new FormData();
      formData.append("video", selectedFile);
      dispatch(
        updateConfig((state) => {
          isMainVideo
            ? (state.isLoadingVideo = true)
            : (state.isArabicVideoLoading = true);
        })
      );
      const response = await getSplashScreen(formData);
      try {
        if (response?.data?.status_code === 200) {
          formik.setFieldValue(
            fieldName === "media_path" ? "media_path" : "alias_media_path",
            response?.data?.data?.video
          );
        } else if (response?.status_code === 422 && response?.errors) {
          Object.keys(response?.errors).forEach((field) => {
            formik.setFieldError(
              `${
                fieldName === "media_path" ? "media_path" : "alias_media_path"
              }`,
              response?.errors[field]
            );
          });
        } else {
          toast.error(`Unable to add video, Please try again.`);
        }
      } catch (error) {
        console.error(`Unable to add video:`, error);
      } finally {
        dispatch(
          updateConfig((state) => {
            isMainVideo
              ? (state.isLoadingVideo = false)
              : (state.isArabicVideoLoading = false);
          })
        );
      }

      const file = selectedFile;
      const reader = new FileReader();
      reader.onloadend = () => {
        dispatch(
          updateConfig((state) => {
            state[
              fieldName === "media_path" ? "videoPreview" : "arabicVideoPreview"
            ] = reader.result;
            state[
              fieldName === "media_path" ? "videoName" : "arabicVideoName"
            ] = selectedFile?.name;
          })
        );
      };

      if (file) {
        reader.readAsDataURL(file);
      }
    }
  };

  const getMinEndDate = () => {
    const startDate = formik?.values?.start_date;
    if (!startDate) return "";

    const date = new Date(startDate);
    date.setDate(date.getDate()); // Add one day

    return date.toISOString().split("T")[0]; // Format as YYYY-MM-DD
  };

  const getMaxDate = () => {
    const endDate = formik?.values?.end_date;
    if (!endDate) return "";

    const date = new Date(endDate);
    date.setDate(date.getDate());

    return date.toISOString().split("T")[0]; // Format as YYYY-MM-DD
  };

  const handleAddSelectedUser = () => {
    if (formik?.values?.add_user !== "" && !formik?.errors?.add_user) {
      if (!formik?.values?.user_mobiles?.includes(formik?.values?.add_user)) {
        formik.setFieldValue("user_mobiles", [
          ...formik.values.user_mobiles,
          formik?.values?.add_user,
        ]);
        formik.setFieldValue("add_user", "");
      } else {
        formik.setFieldError("add_user", "Number is already added");
      }
    }
  };

  const handleRemoveSelectedUser = (index) => {
    if (index > -1 && index < formik?.values?.user_mobiles.length) {
      const updatedUsers = [...formik.values.user_mobiles];
      updatedUsers.splice(index, 1);

      formik.setFieldValue("user_mobiles", updatedUsers);
    }
  };

  return {
    formik,
    isEdit,
    isLoading,
    videoName,
    videoPreview,
    isLoadingVideo,
    arabicVideoName,
    arabicVideoPreview,
    isArabicVideoLoading,
    handleAddSelectedUser,
    handleRemoveSelectedUser,
    handleVideoChange,
    getFieldError,
    closeModal,
    getMinEndDate,
    getMaxDate,
    showAudienceWarning,
    handleConfirmAudienceWarning,
    handleCancelAudienceWarning,
  };
};

export default useAddSplashScreen;
